import React, { useState, useEffect, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

const Phone = ({
  value,
  handleChange,
  name,
  field,
  disabled,
  label = "Phone No",
  country = "US",
}) => {
  const [error, setError] = useState("");
  const [formattedValue, setFormattedValue] = useState(value || "");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(country.toLowerCase());
  const previousPhoneRef = useRef("");

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      setFormattedValue(value);
    }
  }, [value]);

  const validatePhoneNumber = (phone) => {
    try {
      const numeric = String(phone || "").replace(/\D/g, "");
      if (!numeric) return ""; // don't show any error on empty
      // Lightweight validation only: allow any country format; require minimum length
      if (numeric.length < 6) {
        return "Please enter a valid phone number";
      }
      return "";
    } catch (err) {
      console.error("Phone validation error:", err);
      return "";
    }
  };

  const handleInputChange = (phone, countryData) => {
    try {
      const currentCountry = countryData.countryCode?.toLowerCase();
      
      // Update country only when the phone value equals dial code (country just selected)
      // or when country code changes and user is not in the middle of typing
      const phoneDigits = phone.replace(/\D/g, "");
      const dialCodeDigits = countryData.dialCode;
      const isJustDialCode = phoneDigits === dialCodeDigits;
      
      if (currentCountry && currentCountry !== selectedCountry) {
        // If phone is just the dial code, it means country was just selected
        if (isJustDialCode || phoneDigits.length <= dialCodeDigits.length) {
          setSelectedCountry(currentCountry);
        }
      }

      // Only set hasInteracted when user actually types a number beyond dial code
      if (phone.length > countryData.dialCode.length) {
        setHasInteracted(true);
      }

      // Always update the formatted value
      setFormattedValue(phone);
      previousPhoneRef.current = phone;

      // Update the form value - clean number without formatting
      const cleanNumber = phone.replace(/\D/g, "");
      
      // Call form handlers
      if (field?.onChange) {
        field.onChange(cleanNumber);
      }
      if (handleChange) {
        handleChange(cleanNumber, name);
      }
      
      // Clear error while typing
      setError("");
    } catch (err) {
      console.error("Error handling phone input:", err);
      setError("Invalid phone number format");
    }
  };

  // Validate gently on blur only
  const handleBlur = () => {
    const validationError = validatePhoneNumber(formattedValue);
    setError(validationError);
    if (typeof field?.onBlur === "function") {
      try { field.onBlur(); } catch (_) { }
    }
  };

  return (
    <div className="relative" style={{ zIndex: 999 }}>
      <style>
        {`
          .phone-dropdown {
            position: absolute !important;
            z-index: 99999 !important;
            max-height: 300px;
            overflow-y: auto;
            width: 300px;
            margin-top: 1px;
            border: 1px solid #E9E9E9;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .phone-container {
            position: relative;
            z-index: 999;
          }
          .phone-button {
            z-index: 1000;
            cursor: pointer;
            pointer-events: auto;
          }
          .phone-search {
            width: 100%;
            padding: 8px;
            border: none;
            border-bottom: 1px solid #E9E9E9;
          }
          .phone-search:focus {
            outline: none;
          }
          .react-tel-input .flag-dropdown {
            cursor: pointer;
            pointer-events: auto;
          }
          .react-tel-input .country-list .country {
            cursor: pointer;
            pointer-events: auto;
          }
          .react-tel-input .country-list .country:hover {
            background-color: #f5f5f5;
          }
        `}
      </style>
      <PhoneInput
        disabled={disabled}
        country={selectedCountry}
        value={formattedValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        inputProps={{
          name: name,
          ref: field?.ref,
          onBlur: field?.onBlur,
          className: "mt-1 block w-full h-[52px] pl-12 pr-2 border focus:outline-none border-[#E9E9E9] rounded-[4px]"
        }}
        containerClass="phone-container"
        buttonClass="phone-button absolute left-0 top-0 h-full border-r border-[#E9E9E9]"
        dropdownClass="phone-dropdown"
        searchClass="phone-search"
        enableSearch={true}
        disableSearchIcon={true}
        countryCodeEditable={false}
        preferredCountries={['us', 'ca', 'gb', 'pk']}
        enableClickOutside={true}
      />
      {hasInteracted && error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default Phone;
